import { useState } from 'react';
import { CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { RecoveryCodes } from './RecoveryCodes';
import { useTwoFactorSetup } from './useTwoFactorSetup';
import type { AdminUser } from '../../core/types/adminRequests';
type Props = {
  user: AdminUser;
  /** Na het bevestigen (of als hij al aan stond en de codes zijn vernieuwd). */
  onDone: (user: AdminUser) => void;
  /** Alleen als de server overslaan toestaat (`twoFactor.required === false`). */
  onSkip?: () => void;
};
const steps = ['Installeer een authenticator-app op je telefoon, bijvoorbeeld Google Authenticator of Microsoft Authenticator.', 'Scan de QR-code die je hierna ziet.', 'Vul de zes cijfers in die de app toont, en bewaar je herstelcodes.'];

/** Je sleutel in groepjes van vier: makkelijker over te typen. */
const grouped = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

/** Tweestapsverificatie instellen: uitleg, scannen, bevestigen, herstelcodes bewaren. */
export function TwoFactorSetup({
  user,
  onDone,
  onSkip
}: Props) {
  const s = useTwoFactorSetup(user);
  const [saved, setSaved] = useState(false);
  const errorBox = s.error && <p role="alert" className="mt-4 rounded-md bg-blush/60 px-3 py-2 text-[13px] text-ink" data-id="element-3906" data-mp="11serfwaug1fu">
      {s.error}
    </p>;
  if (s.step === 'on') {
    return <div data-id="element-3907" data-mp="1ueziswrbkdje">
        <p className="flex items-center gap-2 text-[15px] text-ink" data-id="element-3908" data-mp="1ugkbegu2pmem">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sage text-shell" aria-hidden="true" data-id="element-3909" data-mp="1zec2j8mw94hr">
            <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-3910" data-mp="1ki4hk63nvz1f" />
          </span>
          Tweestapsverificatie staat aan
        </p>
        <p className="mt-2 max-w-prose text-[14px] leading-relaxed text-muted" data-id="element-3911" data-mp="1pev153eodcrl">
          Bij elke keer inloggen vraagt het beheer na je wachtwoord om de code uit je app. Herstelcodes kwijt? Maak nieuwe; de oude werken dan niet meer.
        </p>
        <button type="button" onClick={() => void s.regenerate()} disabled={s.busy} className={`${btnSecondary} mt-5`} data-id="element-3912" data-mp="2gnodhqgl7pgr">
          {s.busy ? 'Even geduld…' : 'Nieuwe herstelcodes maken'}
        </button>
        {errorBox}
      </div>;
  }
  if (s.step === 'codes') {
    return <div data-id="element-3913" data-mp="21z1osy52llcv">
        <h2 className="font-display text-[1.6rem] font-light leading-tight text-ink" data-id="element-3914" data-mp="21zbe0jiajohi">Bewaar je herstelcodes</h2>
        <p className="mt-2 max-w-prose text-[14px] leading-relaxed text-muted" data-id="element-3915" data-mp="1x0gq535gv9qj">
          Kwijt je je telefoon, dan log je met één van deze codes in. Elke code werkt één keer. Je ziet ze alleen nu; bewaar ze in een wachtwoordkluis of print ze.
        </p>
        <div className="mt-5" data-id="element-3916" data-mp="1o1d4brex0g8c">
          <RecoveryCodes codes={s.codes} data-id="element-3917" data-mp="1j0fpnot464bl" />
        </div>
        <label className="mt-6 flex cursor-pointer items-center gap-3 py-1 text-[14px] text-ink" data-id="element-3918" data-mp="1j20fj14hgz34">
          <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} className="h-5 w-5 accent-clay" data-id="element-3919" data-mp="3icidtcconmvl" />
          Ik heb mijn herstelcodes bewaard
        </label>
        <button type="button" disabled={!saved} onClick={() => {
        s.finish();
        setSaved(false);
        onDone(s.confirmedUser ?? user);
      }} className={`${btnPrimary} mt-5 w-full py-3 sm:w-auto`} data-id="element-3920" data-mp="3kx6ml520yzvo">
          Klaar
        </button>
      </div>;
  }
  if (s.step === 'scan' && s.setup) {
    return <form onSubmit={e => {
      e.preventDefault();
      void s.confirm();
    }} noValidate data-id="element-3921" data-mp="3j2nw6kt14ac">
        <h2 className="font-display text-[1.6rem] font-light leading-tight text-ink" data-id="element-3922" data-mp="36x5bkws1qc2h">Scan de QR-code</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-3923" data-mp="3bwrwlse48wlu">Open je authenticator-app, tik op toevoegen en scan deze code.</p>
        <div className="mt-5 flex flex-col items-start gap-5 sm:flex-row" data-id="element-3924" data-mp="3gxfoshe2zjhn">
          <img src={s.setup.qrImage} alt="QR-code om Beauty Bewust aan je authenticator-app toe te voegen" width={180} height={180} className="h-[180px] w-[180px] rounded-md border border-line bg-shell p-2" data-id="element-3925" data-mp="r60y4mibzfzt" />
          <div className="min-w-0 text-[13px] leading-relaxed text-muted" data-id="element-3926" data-mp="trqutk9sf9z1">
            <p data-id="element-3927" data-mp="pqf3nab9mgoq">Lukt scannen niet? Kies in je app voor “sleutel invoeren” en typ deze over:</p>
            <p className="mt-2 break-all font-mono text-[14px] tracking-wider text-ink" data-id="element-3928" data-mp="13poh42836wp0">{grouped(s.setup.secret)}</p>
          </div>
        </div>
        <label className="mt-6 block max-w-xs" data-id="element-3929" data-mp="yp0jgxpcrhlt">
          <span className="text-[13px] font-medium text-ink" data-id="element-3930" data-mp="1cpl31w33dhgd">Code uit je app</span>
          <input value={s.code} onChange={e => s.setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" aria-invalid={Boolean(s.error)} className={`${inputCls} mt-1.5 py-3 text-center font-mono tracking-[0.3em] sm:text-[18px]`} data-id="element-3931" data-mp="18orb8rmj3ii7" />
        </label>
        {errorBox}
        <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-3932" data-mp="1rfsbdizdxk3c">
          <button type="submit" disabled={s.busy} className={btnPrimary} data-id="element-3933" data-mp="2b0sq6b2pazzc">
            {s.busy ? 'Bezig met controleren…' : 'Bevestigen'}
          </button>
          <button type="button" onClick={s.back} className={btnGhost} data-id="element-3934" data-mp="2f1ui06uag9ra">
            Terug
          </button>
        </div>
      </form>;
  }
  return <div data-id="element-3935" data-mp="261fvj45l8om7">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-shell text-clay ring-1 ring-line" aria-hidden="true" data-id="element-3936" data-mp="2616bryb4zdoy">
        <ShieldCheckIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-3937" data-mp="1rb8odkl16s0t" />
      </span>
      <h2 className="mt-5 font-display text-[1.6rem] font-light leading-tight text-ink" data-id="element-3938" data-mp="1w4jtnha730qm">Beveilig je beheer met je telefoon</h2>
      <p className="mt-2 max-w-prose text-[14px] leading-relaxed text-muted" data-id="element-3939" data-mp="1idyarskqqqsq">
        Hier staan klantgegevens en bestellingen. Met tweestapsverificatie kan niemand inloggen met alleen je wachtwoord. Het kost twee minuten en werkt zonder sms.
      </p>
      <ol className="mt-5 space-y-3" data-id="element-3940" data-mp="1ddjz80jxkypy">
        {steps.map((text, i) => <li key={text} className="flex gap-3 text-[14px] leading-relaxed text-ink" data-id="element-3941" data-mp="u8f5qdkyzj9s" data-mpi={i}>
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sand text-[12px] text-clay" aria-hidden="true" data-id="element-3942" data-mp="ua8fw2usklmj">
              {i + 1}
            </span>
            {text}
          </li>)}
      </ol>
      {errorBox}
      <div className="mt-7 flex flex-wrap items-center gap-2" data-id="element-3943" data-mp="fku3smztzq3g">
        <button type="button" onClick={() => void s.start()} disabled={s.busy} className={btnPrimary} data-id="element-3944" data-mp="1d6zy08ylkup0">
          {s.busy ? 'Even geduld…' : 'Instellen'}
        </button>
        {onSkip && <button type="button" onClick={onSkip} className={btnGhost} data-id="element-3945" data-mp="138ttdjlepagg">
            Overslaan, later instellen
          </button>}
      </div>
    </div>;
}